'use client'
import { useSearchInputManager } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import { JsonsData } from '@/src/types/flight'
import { getFormattedDate } from '@/src/utils/date'
import { SwipeableDrawer } from '@mui/material'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AirportSelector } from './components/airportSelector'
import { DateSelector } from './components/dateSelector'
import { TravelersSelector } from './components/travelersSelector'
import { SearchHeader } from './searchHeader'

interface SearchHeaderWrapperProps {
    srpResultsLoading?: boolean
    open: boolean
    onOpen: () => void
    onClose: () => void
    jsons: JsonsData | undefined
}

const SearchHeaderWrapper: React.FC<SearchHeaderWrapperProps> = ({
    srpResultsLoading,
    open,
    onOpen,
    onClose,
    jsons
}) => {
    // Use the shared hook from desktop for all state management
    const {
        fromToInputsState,
        onAirportSelect,
        isRoundTrip,
        range,
        isRoundTripDispatch,
        rangeDispatch,
        flightClassState,
        travelersState,
        handleTravelerChange,
        handleFlightClassChange,
        updateSearchParams,
        getAirportsFromQuery,
        canIncreaseAdults,
        canIncreaseChildren,
        canIncreaseInfants,
        canDecreaseAdults,
        canDecreaseChildren,
        canDecreaseInfants,
        handleSwap
    } = useSearchInputManager(jsons, srpResultsLoading)

    const { showError } = useThemedToast()
    const t = useTranslations('common')

    // Active component state
    const [activeComponent, setActiveComponent] = useState<'from' | 'to'>('from')
    const [fromToDrawerOpen, setFromToDrawerOpen] = useState(false)
    const [travelersDrawerOpen, setTravelersDrawerOpen] = useState(false)
    const [dateDrawerOpen, setDateDrawerOpen] = useState(false)

    // Validation state for same airport error
    const [sameAirportError, setSameAirportError] = useState(false)

    // Check if from and to airports are the same
    useEffect(() => {
        const fromValue = fromToInputsState.from.value as string
        const toValue = fromToInputsState.to.value as string

        if (fromValue && toValue) {
            // Extract airport codes from the values
            const fromCode = fromValue.split(' - ')[0]
            const toCode = toValue.split(' - ')[0]

            setSameAirportError(fromCode === toCode && fromCode !== '')
        } else {
            setSameAirportError(false)
        }
    }, [fromToInputsState.from.value, fromToInputsState.to.value])

    // Show error toast when airports are the same
    useEffect(() => {
        if (sameAirportError) {
            showError(t('sameAirportError'))
        }
    }, [sameAirportError, showError])

    // Trip type state
    const tripType = useMemo(() => {
        return isRoundTrip ? 'roundtrip' : 'oneway'
    }, [isRoundTrip])

    // Handle trip type change - updates the isRoundTrip state
    const handleTripTypeChange = useCallback(
        (type: 'oneway' | 'roundtrip') => {
            isRoundTripDispatch(type === 'roundtrip')
        },
        [isRoundTripDispatch]
    )
    const { formatDate } = useFormatDate()
    // Format date display
    const formatDateDisplay = useCallback((date: Date | null) => {
        if (!date) return 'Select Date'
        return formatDate(getFormattedDate(date, 'MM/DD/YYYY'), 'ddd, DD MMM')
    }, [])

    // Format travelers display
    const formatTravelersDisplay = useCallback(() => {
        const total = travelersState.adults + travelersState.childs + travelersState.infants

        // Use the format: {total} {traveller/travellers}
        if (total > 1) {
            return `${total} ${t('traveller', { s: 's' })}`
        } else {
            return `${total} ${t('traveller', { s: '' })}`
        }
    }, [travelersState, t])

    // Handle component activations
    const handleFromClick = useCallback(() => {
        setFromToDrawerOpen(true)
        setActiveComponent('from')
    }, [])

    const handleToClick = useCallback(() => {
        setFromToDrawerOpen(true)
        setActiveComponent('to')
    }, [])
    const handleDateClick = useCallback(() => {
        setDateDrawerOpen(true)
    }, [])

    const handleTravelersClick = useCallback(() => {
        setTravelersDrawerOpen(true)
    }, [])

    // Handle search - updates URL and refreshes the page with new search
    const handleSearch = useCallback(() => {
        // Don't search if there's a validation error
        if (sameAirportError) {
            return
        }
        updateSearchParams()
        onClose()
    }, [updateSearchParams, sameAirportError])

    return (
        <>
            <SearchHeader
                open={open}
                onOpen={onOpen}
                onClose={onClose}
                tripType={tripType}
                onTripTypeChange={handleTripTypeChange}
                fromLocation={fromToInputsState.from.value}
                toLocation={fromToInputsState.to.value}
                departDate={formatDateDisplay(range?.from || null)}
                returnDate={isRoundTrip ? formatDateDisplay(range?.to || null) : undefined}
                travelers={formatTravelersDisplay()}
                flightClass={Array.isArray(flightClassState) ? flightClassState[0] : flightClassState}
                onFromClick={handleFromClick}
                onToClick={handleToClick}
                onDateClick={handleDateClick}
                onTravelersClick={handleTravelersClick}
                onSearch={handleSearch}
                onSwap={handleSwap}
                hasError={sameAirportError}
                srpResultsLoading={srpResultsLoading}
            />

            {/* Airport Selector Modal - Rendered outside of drawer */}
            <SwipeableDrawer
                anchor="right"
                open={fromToDrawerOpen}
                onClose={() => setTravelersDrawerOpen(false)}
                onOpen={() => setFromToDrawerOpen(true)}
                slotProps={{
                    paper: {
                        sx: {
                            width: '100%',
                            height: '100%'
                        }
                    }
                }}
                ModalProps={{
                    keepMounted: false
                }}
            >
                <AirportSelector
                    isPopularAirports={
                        activeComponent === 'from'
                            ? fromToInputsState.from.isPopularAirports
                            : fromToInputsState.to.isPopularAirports
                    }
                    type={activeComponent}
                    value={
                        activeComponent === 'from'
                            ? (fromToInputsState.from.value as string)
                            : (fromToInputsState.to.value as string)
                    }
                    loading={activeComponent === 'from' ? fromToInputsState.from.loading : fromToInputsState.to.loading}
                    options={activeComponent === 'from' ? fromToInputsState.from.options : fromToInputsState.to.options}
                    onSearch={getAirportsFromQuery}
                    onSelect={(value, type) => {
                        onAirportSelect(value, type)
                    }}
                    visible={true}
                    onClose={() => setFromToDrawerOpen(false)}
                />
            </SwipeableDrawer>

            {/* Date Selector Drawer */}
            <DateSelector
                open={dateDrawerOpen}
                onOpen={() => setDateDrawerOpen(true)}
                onClose={() => setDateDrawerOpen(false)}
                isRoundTrip={isRoundTrip}
                range={range}
                onDateSelect={rangeDispatch}
            />

            {/* Travelers Selector Drawer */}
            <TravelersSelector
                open={travelersDrawerOpen}
                onOpen={() => setTravelersDrawerOpen(true)}
                onClose={() => setTravelersDrawerOpen(false)}
                adults={travelersState.adults}
                children={travelersState.childs}
                infants={travelersState.infants}
                flightClass={Array.isArray(flightClassState) ? flightClassState[0] : flightClassState}
                onTravelerChange={handleTravelerChange}
                onFlightClassChange={handleFlightClassChange}
                canIncreaseAdults={canIncreaseAdults(travelersState)}
                canIncreaseChildren={canIncreaseChildren(travelersState)}
                canIncreaseInfants={canIncreaseInfants(travelersState)}
                canDecreaseAdults={canDecreaseAdults(travelersState)}
                canDecreaseChildren={canDecreaseChildren(travelersState)}
                canDecreaseInfants={canDecreaseInfants(travelersState)}
                onApply={() => setTravelersDrawerOpen(false)}
            />
        </>
    )
}

export { SearchHeaderWrapper }
export default SearchHeaderWrapper
